import frontendUtils from 'honda_cars_ecux/utils/frontendUtils';

/**
 * 2D rectangle in the container's local coordinate space.
 *
 * @typedef {{ x: number, y: number, w: number, h: number }} Rect
 *
 * Per-layer state for one indicator. Both `active` layers share this shape; `prefix`
 * namespaces their CSS custom properties (`--active-x`, …).
 *
 * @typedef {object} ManhattanLayer
 * @property {'active'} prefix
 * @property {HTMLElement | null} wrapper   Outer element — drives X-axis (translate-x / width).
 * @property {HTMLElement | null} inner     Inner span — drives Y-axis (translate-y / height).
 * @property {Rect} target                  Destination of the next/in-flight move.
 * @property {Rect} position                Last-committed position; cached to avoid `getComputedStyle` reads in event handlers.
 * @property {HTMLElement | null} lastLabel
 */
export const changeModel = {
  /**
   * Bootstrap the change-model view's animated indicator. Idempotent; safe to call once at boot.
   */
  changeModelView() {
    this.helpers.bootstrap();
  },

  /**
   * Controller for the dual-layer "Manhattan Motion" indicator. Each layer is a `wrapper > inner`
   * pair (wrapper = X, inner = Y); the X step hands off to Y on the wrapper's `transitionend`
   * for the signature L-shaped travel. CSS owns the animation; this controller just writes the
   * CSS variables.
   */
  helpers: {
    /**
     * Constant velocity for the indicator, in pixels per millisecond. Tune up for snappier feel.
     *
     * @readonly
     */
    velocityPxPerMs: 1,

    /**
     * Floor on per-axis duration so short moves still feel intentional and `transitionend` always fires.
     *
     * @readonly
     */
    minDurationMs: 200,

    /**
     * Query keys tied to the current vehicle. Dropped on model change so the next PDP
     * seeds grade, colour, campaign, and accessories. `pid` stays off the copy because
     * the selected card href already carries the new model id.
     *
     * @private
     * @readonly
     *
     * @type {string[]}
     */
    modelSpecificParams: ['pid', 'camp', 'm', 'v', 'sv', 'accpk', 'acc', 'mem'],

    /**
     * @type {boolean}
     */
    isWired: false,

    /**
     * @type {{
     *   container: HTMLElement | null,
     *   modal: HTMLElement | null,
     *   changeModelBtn: HTMLButtonElement | null
     * }}
     */
    elements: {
      container: null,
      modal: null,
      changeModelBtn: null
    },

    /**
     * @type {Record<'active', ManhattanLayer>}
     */
    layers: {
      active: {
        prefix: 'active',
        wrapper: null,
        inner: null,
        target: { x: 0, y: 0, w: 0, h: 0 },
        position: { x: 0, y: 0, w: 0, h: 0 },
        lastLabel: null
      }
    },

    /**
     * Modal hooks — wires on first show and re-snaps the indicator whenever the view (re-)appears
     * or the viewport resizes, so it can never drift off-target.
     */
    bootstrap() {
      document.body.addEventListener('amb:modal-view-changed', event => {
        if (event.detail?.view === 'change-model' && this.wire()) {
          this.snapToChecked();
        }
      });
    },

    /**
     * Resolve element refs and attach DOM listeners. No-op after the first successful run.
     *
     * @returns {boolean} `true` once wiring has succeeded.
     */
    wire() {
      if (this.isWired) {
        return true;
      }

      const container = document.querySelector('.configurator-modal .model-list');

      if (!container) {
        return false;
      }

      this.elements.container = container;
      this.elements.modal = container.closest('.configurator-modal');
      this.elements.changeModelBtn = container.closest('.modal-view')?.querySelector('.btn-change-model') ?? null;

      this.layers.active.wrapper = container.querySelector('.animation.active');
      this.layers.active.inner = this.layers.active.wrapper?.querySelector('.inner') ?? null;

      if (!this.layers.active.wrapper) {
        return false;
      }

      container.addEventListener('change', event => this.onActiveModel(event));
      this.elements.changeModelBtn?.addEventListener('click', event => this.onConfirmModel(event));

      this.attachTransitionRelay(this.layers.active);

      let resizeTimeout;
      const sizeObserver = new ResizeObserver(() => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => this.snapToChecked(false), 50);
      });
      sizeObserver.observe(this.elements.container.closest('.modal-body'));

      this.isWired = true;

      return true;
    },

    /**
     * Single bubbling `transitionend` listener handles both legs:
     *   - target = wrapper → X done, hand off to {@link changeModel.helpers.moveVertical}
     *   - target = inner   → Y done; for the active layer, commit `.active` to the resting card
     *
     * @param {ManhattanLayer} layer
     */
    attachTransitionRelay(layer) {
      layer.wrapper.addEventListener('transitionend', event => {
        if (event.propertyName !== 'translate') {
          return;
        }

        if (event.target === layer.wrapper) {
          this.moveVertical(layer);
        } else if (event.target === layer.inner && layer.prefix === 'active') {
          this.activateCard(layer.lastLabel);
        }
      });
    },

    /**
     * Radio `change` delegate — reflects the *preview* selection. Strips `.active` from the previous
     * card immediately, then kicks off the indicator move; the new card's `.active` is added when the
     * indicator lands (see {@link changeModel.helpers.attachTransitionRelay}).
     *
     * @param {Event} event
     */
    onActiveModel(event) {
      const input = event.target.closest('.model--input');
      const selectedModel = input.dataset.productId;
      const currentModel = this.elements.container.dataset.currentModel;

      if (!input || !input.checked) {
        return;
      }

      const label = this.findLabelFor(input);

      if (!label) {
        return;
      }

      this.elements.changeModelBtn?.toggleAttribute('disabled', !selectedModel || selectedModel === currentModel);
      this.layers.active.lastLabel = label;
      this.deactivateAll();
      this.targetActive(label);
      this.moveHorizontal(this.layers.active);

      label.closest('.model-card').scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    },

    /**
     * `.btn-change-model` click — commits the preview by navigating to the selected model's PDP when
     * it differs from the current one. Passthrough params from the current location are merged in so
     * an in-flight order flow survives the navigation. Clears `dapModalShown` so a drive-away price
     * failure on the next model can open the error modal again.
     *
     * @param {MouseEvent} event
     */
    onConfirmModel(event) {
      const checked = this.elements.container.querySelector('.model--input:checked');

      if (!checked || !checked.value) {
        return;
      }

      const selectedId = checked.dataset.productId;
      const currentId = this.elements.container.dataset.currentModel;

      if (!selectedId || selectedId === currentId) {
        return;
      }

      try {
        sessionStorage.setItem('modelChangedToast', '1');
      } catch (e) {
        /* sessionStorage unavailable — toast on next paint will be skipped */
      }

      frontendUtils.deleteCookie('dapModalShown');
      window.location.assign(this.withOrderFlowParams(checked.value));
    },

    /**
     * Copy the current query onto `targetUrl`, dropping vehicle-specific keys.
     * Runs for mid-order (`orderId` + `modifyCar`/`action`), dealer view (`sessionId`),
     * or a CXP lead/opportunity. First-time customer URLs stay clean.
     *
     * @param {string} targetUrl
     * @returns {string}
     */
    withOrderFlowParams(targetUrl) {
      const current = new URLSearchParams(window.location.search);
      const isMidOrderFlow = current.has('orderId') && (current.has('modifyCar') || current.has('action'));
      const isDealerView = current.has('sessionId');
      const recordType = current.get('relatedRecordType');
      const isDealerRecord = recordType === 'Lead' || recordType === 'Opportunity';

      if (!isMidOrderFlow && !isDealerView && !isDealerRecord) {
        return targetUrl;
      }

      const url = new URL(targetUrl, window.location.origin);

      current.forEach((value, key) => {
        if (!this.modelSpecificParams.includes(key)) {
          url.searchParams.append(key, value);
        }
      });

      return url.toString();
    },

    /**
     * Update the active layer's target rect from a label, without animating.
     *
     * @param {HTMLElement} label
     */
    targetActive(label) {
      this.layers.active.target = this.measureLabel(label);
    },

    /**
     * Pixel distance → transition duration in ms (constant velocity, with a min-duration floor).
     *
     * @param {number} distancePx
     * @returns {number}
     */
    durationFor(distancePx) {
      return Math.max(this.minDurationMs, distancePx / this.velocityPxPerMs);
    },

    /**
     * STEP 1 — Animate the X axis. Same-column case (≤2px) skips X and calls
     * {@link changeModel.helpers.moveVertical} directly; otherwise the wrapper's `transitionend`
     * relay does the handoff.
     *
     * @param {ManhattanLayer} layer
     */
    moveHorizontal(layer) {
      const container = this.elements.container;
      const xDistance = Math.abs(layer.target.x - layer.position.x);

      container.style.setProperty(`--${layer.prefix}-w`, `${layer.target.w}px`);

      if (xDistance > 2) {
        container.style.setProperty(`--${layer.prefix}-x-duration`, `${this.durationFor(xDistance)}ms`);
        container.style.setProperty(`--${layer.prefix}-x`, `${layer.target.x}px`);
        layer.position.x = layer.target.x;
        layer.position.w = layer.target.w;
      } else {
        this.moveVertical(layer);
      }
    },

    /**
     * STEP 2 — Animate the Y axis. Mirrors `moveHorizontal`'s skip-if-tiny pattern: when there's no
     * Y travel the inner's `transitionend` would never fire, so the active layer commits `.active`
     * inline here.
     *
     * @param {ManhattanLayer} layer
     */
    moveVertical(layer) {
      const container = this.elements.container;
      const yDistance = Math.abs(layer.target.y - layer.position.y);

      if (yDistance > 2) {
        container.style.setProperty(`--${layer.prefix}-y-duration`, `${this.durationFor(yDistance)}ms`);
      } else if (layer.prefix === 'active') {
        this.activateCard(layer.lastLabel);
      }

      container.style.setProperty(`--${layer.prefix}-y`, `${layer.target.y}px`);
      container.style.setProperty(`--${layer.prefix}-h`, `${layer.target.h}px`);
      layer.position.y = layer.target.y;
      layer.position.h = layer.target.h;
    },

    /**
     * Add `.active` to the card containing `label`, triggering its post-arrival reveal
     * (check fade-in, badge color shift) — see `.model-card.active` in `_change-model.scss`.
     *
     * @param {HTMLElement | null} label
     */
    activateCard(label) {
      label?.closest('.model-card')?.classList.add('active');
    },

    /**
     * Strip `.active` from every card. Called on selection change and on snap.
     */
    deactivateAll() {
      this.elements.container.querySelectorAll('.model-card.active').forEach(card => {
        card.classList.remove('active');
      });
    },

    /**
     * Snap both indicator layers AND the `.active` class onto the currently-checked radio with
     * transitions disabled. Used on first show, modal re-open, and resize.
     */
    snapToChecked(immediately = true) {
      if (!this.isWired) {
        return;
      }

      const container = this.elements.container;
      const checkedInput = container.querySelector('.model--input:checked');

      if (!checkedInput) {
        return;
      }

      const label = this.findLabelFor(checkedInput);

      if (!label) {
        return;
      }


      if (immediately) {
        const target = this.measureLabel(label);
        container.classList.add('no-transition');
        this.applyTargetImmediately(this.layers.active, target);
        container.offsetWidth;
        container.classList.remove('no-transition');
      } else {
        this.targetActive(label);
        this.moveHorizontal(this.layers.active);
      }

      this.deactivateAll();
      this.activateCard(label);

      this.layers.active.lastLabel = label;
    },

    /**
     * Write a target rect straight into a layer's CSS variables, bypassing the X→Y relay. Caller is
     * responsible for the `no-transition` window.
     *
     * @param {ManhattanLayer} layer
     * @param {Rect} target
     */
    applyTargetImmediately(layer, target) {
      const container = this.elements.container;

      layer.target = { ...target };
      layer.position = { ...target };

      container.style.setProperty(`--${layer.prefix}-x`, `${target.x}px`);
      container.style.setProperty(`--${layer.prefix}-y`, `${target.y}px`);
      container.style.setProperty(`--${layer.prefix}-w`, `${target.w}px`);
      container.style.setProperty(`--${layer.prefix}-h`, `${target.h}px`);
    },

    /**
     * Read a label's box in the model-list's coordinate space.
     *
     * @param {HTMLElement} label
     * @returns {Rect}
     */
    measureLabel(label) {
      const card = label.closest('.model-card') || label;

      return {
        x: card.offsetLeft,
        y: card.offsetTop,
        w: card.offsetWidth,
        h: card.offsetHeight
      };
    },

    /**
     * Resolve the `<label>` paired with a radio. Prefers `for=`, falls back to `nextElementSibling`.
     *
     * @param {HTMLInputElement} input
     * @returns {HTMLElement | null}
     */
    findLabelFor(input) {
      if (input.id) {
        const byFor = this.elements.container.querySelector(`label[for="${input.id}"]`);

        if (byFor) {
          return byFor;
        }
      }

      const sibling = input.nextElementSibling;
      return sibling && sibling.matches('.model--label') ? sibling : null;
    }
  }
};
